import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger.js'
gsap.registerPlugin(ScrollTrigger)

/**
 * Sequence Scroller
 *
 * Scroll-driven canvas image sequence renderer.
 *
 * What this class is responsible for:
 * - Convert scroll progress to a target frame.
 * - Load image frames from config-defined sets (mediaQuery + path + filenameTemplate).
 * - Draw only loaded frames and keep the current frame visible if target is not ready yet.
 * - Emit animation-scroller:update state events for external listeners (for example slide logic).
 *
 * Key options:
 * - el: root animation scroller element.
 * - frameCount: total frame count in the sequence.
 * - sets: asset definitions with mediaQuery, path, filenameTemplate.
 * - scrollDistanceVh and scrub: ScrollTrigger behavior.
 * - preloadStrategy, preloadRadius, initialPreloadCount, preloadSpreadDepth, maxCacheSize: loading behavior.
 * - onStateChange: optional callback receiving current scroller state.
 *
 * Notes:
 * - Keep this class focused on rendering and frame loading only.
 * - Keep text/slide transitions in a separate class listening to emitted state.
 *
 *
 * Config examples:
 * 1) Frame mode, open-ended start
 * {
 * 	"frames": 230,
 * 	"slideMode": "frame",
 * 	"slideThresholds": [[0], [150], [200]]
 * }
 *
 * 2) Frame mode, explicit ranges
 * {
 * 	"frames": 230,
 * 	"slideMode": "frame",
 * 	"slideThresholds": [[0, 80], [120, 180], [200, 229]]
 * }
 *
 * 3) Progress mode, open-ended starts
 * {
 * 	"frames": 230,
 * 	"slideMode": "progress",
 * 	"slideThresholds": [[0], [0.35], [0.7]]
 * }
 *
 * 4) Progress mode, explicit ranges
 * {
 * 	"frames": 230,
 * 	"slideMode": "progress",
 * 	"slideThresholds": [[0.0, 0.2], [0.4, 0.55], [0.8, 1.0]]
 * }
 *
 * 5) Full example with sets + slides
 * {
 * 	"frames": 230,
 * 	"sets": [
 * 		{
 * 			"mediaQuery": "default",
 * 			"path": "/wp-content/uploads/animations/animation-1/",
 * 			"filenameTemplate": "scroll_animation_1_{00000}.{ext}"
 * 		},
 * 		{
 * 			"mediaQuery": "(orientation: portrait)",
 * 			"path": "/wp-content/uploads/animations/animation-1/",
 * 			"filenameTemplate": "scroll_animation_1_{00000}.{ext}"
 * 		}
 * 	],
 * 	"slideMode": "frame",
 * 	"slideThresholds": [[0], [150], [200]]
 * }
 **/

export class SequenceScroller {
	constructor(options) {
		this.options = {
			scrollDistanceVh: 220,
			scrub: 0.15,
			preloadRadius: 8,
			preloadStrategy: 'linear', // linear | spread
			initialPreloadCount: 18, // how many frames to request during boot
			preloadSpreadDepth: 6, // recursion depth for spread strategy
			continuePreloadingAfterInitial: true, // whether to keep preloading in the background after the initial batch
			continuousPreloadConcurrency: 24,
			continuousPreloadStartDelayMs: 500, // delay before starting continuous background preload in ms
			continuousPreloadDelayMs: 0, // delay between continuous preload batches in ms
			maxCacheSize: -1, // max number of frames to keep in memory, -1 for unlimited
			onStateChange: null,
			enableWebp: false, // feature toggle per project
			defaultImageExtension: 'jpg', // used when webp is disabled or unavailable
			disableWebpAfterFirstMiss: true, // avoid repeated webp 404s
			...options,
		}

		this.el = this.options.el
		this.canvas = this.el.querySelector('[data-animation-canvas]')
		this.ctx = null

		this.frameCount = this.options.frameCount || 0
		this.preloadStrategy = this.options.preloadStrategy
		this.initialPreloadCount = this.options.initialPreloadCount
		this.preloadSpreadDepth = this.options.preloadSpreadDepth
		this.backgroundPreloadRunId = 0
		this.backgroundPreloadStartTimer = 0

		this.currentFrame = 0
		this.targetFrame = 0
		this.currentProgress = 0
		this.previousProgress = 0
		this.direction = 1

		this.resizeRaf = 0
		this.trigger = null

		this.webpSupported = null // null = not checked yet
		this.webpAssetsMissing = false

		this.isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
		this.images = new Map()
		this.loading = new Map()

		this.baseViewportHeight = 0
		this.lastViewportWidth = 0
		this.lastOrientation = ''
		this.activeSetKey = ''

		this.onResize = this.onResize.bind(this)

		this.captureViewportState()
	}

	init() {
		if (!this.canvas || this.frameCount < 2) {
			return
		}

		this.ctx = this.canvas.getContext('2d', { alpha: true })
		if (!this.ctx) {
			return
		}

		this.setupCanvasSize()
		window.addEventListener('resize', this.onResize, { passive: true })

		if (this.isReducedMotion) {
			this.loadFrame(0).then(() => {
				this.drawFrame(0)
				this.emitStateChange()
			})
			return
		}

		this.loadFrame(0).then(() => {
			this.drawFrame(0)
			this.emitStateChange()
		})
		this.preloadInitialBatch().finally(() => this.startBackgroundPreload())

		this.setupScrollTrigger()
	}

	destroy() {
		window.removeEventListener('resize', this.onResize)
		this.stopBackgroundPreload()
		if (this.trigger) {
			this.trigger.kill()
			this.trigger = null
		}

		if (this.resizeRaf) {
			cancelAnimationFrame(this.resizeRaf)
			this.resizeRaf = 0
		}

		this.images.clear()
		this.loading.clear()
	}

	getConfigSets() {
		const sets = this.options.sets || this.options.assets || []
		return Array.isArray(sets) ? sets : []
	}
	getActiveSetKey(set) {
		if (!set) return ''
		return [set.mediaQuery || 'default', set.path || '', set.filenameTemplate || ''].join('|')
	}
	getActiveSet() {
		const sets = this.getConfigSets()

		if (!sets.length) return null

		// SSR or non-browser safety
		if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
			return sets[0]
		}

		let fallback = null

		for (let i = 0; i < sets.length; i += 1) {
			const set = sets[i]
			const mq = (set.mediaQuery || '').trim()

			// explicit default
			if (!mq || mq.toLowerCase() === 'default') {
				if (!fallback) fallback = set
				continue
			}

			try {
				if (window.matchMedia(mq).matches) {
					return set
				}
			} catch (e) {
				// invalid media query: ignore and continue
			}
		}

		return fallback || sets[0]
	}

	getOrientation() {
		return window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape'
	}

	captureViewportState() {
		this.baseViewportHeight = window.innerHeight
		this.lastViewportWidth = window.innerWidth
		this.lastOrientation = this.getOrientation()
		this.activeSetKey = this.getActiveSetKey(this.getActiveSet())
	}

	hasMeaningfulResize() {
		const width = window.innerWidth
		const orientation = this.getOrientation()

		const widthChanged = Math.abs(width - this.lastViewportWidth) > 1
		const orientationChanged = orientation !== this.lastOrientation

		// Update snapshot for next comparison.
		this.lastViewportWidth = width
		this.lastOrientation = orientation

		// Ignore iOS toolbar show/hide height-only resize noise.
		return widthChanged || orientationChanged
	}

	setupScrollTrigger() {
		this.trigger = ScrollTrigger.create({
			trigger: this.el,
			start: 'top top',
			end: () => '+=' + Math.round(window.innerHeight * (this.options.scrollDistanceVh / 100)),
			pin: true,
			scrub: this.options.scrub,
			invalidateOnRefresh: true,
			ignoreMobileResize: true,
			onUpdate: (self) => {
				this.setProgress(self.progress)
			},
		})
	}

	setProgress(progress) {
		const maxFrame = this.frameCount - 1
		const frame = Math.max(0, Math.min(maxFrame, Math.round(progress * maxFrame)))

		this.previousProgress = this.currentProgress
		this.currentProgress = progress
		this.direction = this.currentProgress >= this.previousProgress ? 1 : -1

		// Same target frame: if it became available meanwhile, draw it now.
		if (frame === this.targetFrame) {
			if (this.currentFrame !== frame && this.images.has(frame)) {
				this.drawFrame(frame)
			}
			this.emitStateChange()
			return
		}

		this.targetFrame = frame

		// Only draw immediately if already loaded. Otherwise keep current visible.
		if (this.images.has(frame)) {
			this.drawFrame(frame)
		} else {
			// Request only, no draw on load callback.
			this.loadFrame(frame).catch(() => {})
		}

		this.preloadAround(frame)
		this.emitStateChange()
	}

	getStatePayload() {
		return {
			frame: this.currentFrame,
			targetFrame: this.targetFrame,
			progress: this.currentProgress,
			direction: this.direction,
			frameCount: this.frameCount,
		}
	}

	emitStateChange() {
		const payload = this.getStatePayload()

		if (typeof this.options.onStateChange === 'function') {
			this.options.onStateChange(payload)
		}

		this.el.dispatchEvent(
			new CustomEvent('animation-scroller:update', {
				bubbles: true,
				detail: payload,
			}),
		)
	}

	startBackgroundPreload() {
		if (!this.options.continuePreloadingAfterInitial) return

		const runId = ++this.backgroundPreloadRunId
		const startDelayMs = Math.max(0, Number(this.options.continuousPreloadStartDelayMs) || 0)

		if (this.backgroundPreloadStartTimer) {
			clearTimeout(this.backgroundPreloadStartTimer)
			this.backgroundPreloadStartTimer = 0
		}

		const run = () => {
			if (runId !== this.backgroundPreloadRunId) return
			this.runBackgroundPreload(runId).catch(() => {})
		}

		if (startDelayMs > 0) {
			this.backgroundPreloadStartTimer = setTimeout(() => {
				this.backgroundPreloadStartTimer = 0
				run()
			}, startDelayMs)
		} else {
			run()
		}
	}

	stopBackgroundPreload() {
		this.backgroundPreloadRunId += 1

		if (this.backgroundPreloadStartTimer) {
			clearTimeout(this.backgroundPreloadStartTimer)
			this.backgroundPreloadStartTimer = 0
		}
	}

	async runBackgroundPreload(runId) {
		const order = this.getInitialPreloadOrder()
		const queue = order.filter((index) => !this.images.has(index) && !this.loading.has(index))
		const concurrency = Math.max(1, Number(this.options.continuousPreloadConcurrency) || 1)
		const delayMs = Math.max(0, Number(this.options.continuousPreloadDelayMs) || 0)

		const worker = async () => {
			while (queue.length && runId === this.backgroundPreloadRunId) {
				const index = queue.shift()
				if (typeof index !== 'number') return
				await this.loadFrame(index).catch(() => {})
				if (delayMs > 0) {
					await new Promise((resolve) => setTimeout(resolve, delayMs))
				}
			}
		}

		const workers = []
		for (let i = 0; i < concurrency; i += 1) {
			workers.push(worker())
		}
		await Promise.allSettled(workers)
	}
	async preloadInitialBatch() {
		const order = this.getInitialPreloadOrder()
		const count = Math.max(0, Math.min(this.frameCount, this.initialPreloadCount))
		const selected = order.slice(0, count)

		const promises = selected.map((index) => this.loadFrame(index))
		await Promise.allSettled(promises)
		// const max = Math.min(this.frameCount - 1, this.options.preloadRadius * 2)
		// const promises = []

		// for (let i = 1; i <= max; i += 1) {
		// 	promises.push(this.loadFrame(i))
		// }

		// await Promise.allSettled(promises)
	}

	getInitialPreloadOrder() {
		if (this.frameCount < 1) return []

		if (this.preloadStrategy === 'spread') {
			console.log('Using spread preload strategy')
			return this.createSpreadOrder()
		}

		return this.createLinearOrder()
	}

	createLinearOrder() {
		const order = []
		for (let i = 0; i < this.frameCount; i += 1) {
			order.push(i)
		}
		return order
	}

	createSpreadOrder() {
		const max = this.frameCount - 1
		const order = []
		const seen = new Set()

		const push = (index) => {
			if (index < 0 || index > max || seen.has(index)) return
			seen.add(index)
			order.push(index)
		}

		const center = Math.round(max / 2)
		push(0)
		push(max)
		push(center)

		let ranges = [
			[0, center],
			[center, max],
		]

		let depth = 0
		while (ranges.length && depth < this.preloadSpreadDepth) {
			const nextRanges = []

			for (let i = 0; i < ranges.length; i += 1) {
				const start = ranges[i][0]
				const end = ranges[i][1]

				if (end - start <= 1) continue

				const mid = Math.round((start + end) / 2)
				push(mid)

				if (mid - start > 1) nextRanges.push([start, mid])
				if (end - mid > 1) nextRanges.push([mid, end])
			}

			ranges = nextRanges
			depth += 1
		}

		// Ensure any remaining gaps are appended so order can cover all frames.
		for (let i = 0; i <= max; i += 1) {
			push(i)
		}

		return order
	}

	preloadAround(center) {
		const radius = this.options.preloadRadius
		const toLoad = []

		for (let i = center - radius; i <= center + radius; i += 1) {
			if (i < 0 || i >= this.frameCount) continue
			if (this.images.has(i) || this.loading.has(i)) continue
			toLoad.push(i)
		}

		toLoad.forEach((index) => this.loadFrame(index))
		this.pruneCache(center)
	}

	pruneCache(center) {
		const maxCacheSize = Number(this.options.maxCacheSize)

		// -1 means unlimited cache (never prune)
		if (maxCacheSize === -1) {
			return
		}

		// Guard invalid values
		if (!Number.isFinite(maxCacheSize) || maxCacheSize < 1) {
			return
		}

		if (this.images.size <= maxCacheSize) {
			return
		}

		const entries = Array.from(this.images.keys())
		entries.sort((a, b) => Math.abs(a - center) - Math.abs(b - center))

		const keep = new Set(entries.slice(0, maxCacheSize))
		entries.forEach((index) => {
			if (!keep.has(index)) {
				this.images.delete(index)
			}
		})
	}

	async loadFrame(index) {
		if (this.images.has(index)) {
			return this.images.get(index)
		}

		if (this.loading.has(index)) {
			return this.loading.get(index)
		}

		const promise = (async () => {
			const candidates = this.buildFrameUrlCandidates(index)
			let lastError = null

			for (let i = 0; i < candidates.length; i += 1) {
				const candidate = candidates[i]
				const isWebpCandidate = /\.webp($|\?)/i.test(candidate)

				try {
					const img = await this.loadImageFromUrl(candidate)
					this.images.set(index, img)
					return img
				} catch (error) {
					lastError = error

					if (isWebpCandidate && this.options.disableWebpAfterFirstMiss) {
						this.webpAssetsMissing = true
					}
				}
			}

			throw lastError || new Error('Failed to load frame ' + index)
		})().finally(() => {
			this.loading.delete(index)
		})

		this.loading.set(index, promise)
		return promise
	}

	loadImageFromUrl(url) {
		return new Promise((resolve, reject) => {
			const img = new Image()
			img.decoding = 'async'
			img.onload = () => resolve(img)
			img.onerror = () => reject(new Error('Failed to load image: ' + url))
			img.src = url
		})
	}

	normalizeExtension(ext) {
		const raw = String(ext || '')
			.trim()
			.toLowerCase()
		return raw.replace(/^\./, '') || 'jpg'
	}

	supportsWebp() {
		if (this.webpSupported !== null) {
			return this.webpSupported
		}

		if (!this.options.enableWebp) {
			this.webpSupported = false
			return this.webpSupported
		}

		try {
			const canvas = document.createElement('canvas')
			this.webpSupported = canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0
		} catch (e) {
			this.webpSupported = false
		}

		return this.webpSupported
	}

	templateWithExtension(template, ext) {
		const safeExt = this.normalizeExtension(ext)
		const asString = String(template || '')

		if (asString.includes('{ext}')) {
			return asString.replace(/\{ext\}/g, safeExt)
		}

		if (/\.[a-z0-9]+$/i.test(asString)) {
			return asString.replace(/\.[a-z0-9]+$/i, '.' + safeExt)
		}

		return asString + '.' + safeExt
	}

	buildFrameUrlByExtension(index, ext) {
		const set = this.getActiveSet()
		if (!set) return ''

		const template = this.templateWithExtension(set.filenameTemplate, ext)
		const filename = this.buildFilenameFromTemplate(template, index)
		return set.path + filename
	}

	buildFrameUrlCandidates(index) {
		const set = this.getActiveSet()
		if (!set) return []

		const fallbackExt = this.normalizeExtension(set.defaultImageExtension || this.options.defaultImageExtension)
		const candidates = []
		const canTryWebp = this.supportsWebp() && !this.webpAssetsMissing

		if (canTryWebp) {
			candidates.push(this.buildFrameUrlByExtension(index, 'webp'))
		}

		candidates.push(this.buildFrameUrlByExtension(index, fallbackExt))

		// de-dupe in case fallbackExt is also webp
		return Array.from(new Set(candidates.filter(Boolean)))
	}

	parseFilenameTemplate(template) {
		// Example: "scroll_animation_1_{00000}.jpg"
		const match = String(template || '').match(/^(.*)\{(0+)\}(.*)$/)

		if (!match) {
			// no token -> static filename
			return {
				prefix: String(template || ''),
				pad: 0,
				suffix: '',
			}
		}

		return {
			prefix: match[1], // "scroll_animation_1_"
			pad: match[2].length, // 5
			suffix: match[3], // ".jpg"
		}
	}

	buildFilenameFromTemplate(template, frameIndex) {
		const parsed = this.parseFilenameTemplate(template)
		if (parsed.pad === 0) return parsed.prefix

		const frame = String(frameIndex).padStart(parsed.pad, '0')
		return parsed.prefix + frame + parsed.suffix
	}

	buildFrameUrl(index) {
		const set = this.getActiveSet() // from options.sets
		if (!set) return ''

		const filename = this.buildFilenameFromTemplate(set.filenameTemplate, index)
		return set.path + filename
		// const base = window.innerWidth >= this.breakpoint ? this.pathDesktop : this.pathMobile
		// const num = String(index).padStart(this.pad, '0')
		// return base + this.prefix + num + '.' + this.ext
	}

	drawFrame(index) {
		const img = this.images.get(index)
		if (!img || !this.ctx) {
			return
		}

		this.currentFrame = index
		const ctx = this.ctx
		const cW = this.canvas.width
		const cH = this.canvas.height

		ctx.clearRect(0, 0, cW, cH)

		const scale = Math.max(cW / img.naturalWidth, cH / img.naturalHeight)
		const w = img.naturalWidth * scale
		const h = img.naturalHeight * scale
		const x = (cW - w) * 0.5
		const y = (cH - h) * 0.5

		ctx.drawImage(img, x, y, w, h)
	}

	setupCanvasSize() {
		const dpr = Math.min(window.devicePixelRatio || 1, 2)
		const width = Math.max(1, Math.round(this.el.clientWidth * dpr))
		const height = Math.max(1, Math.round(this.el.clientHeight * dpr))

		if (this.canvas.width === width && this.canvas.height === height) {
			return
		}

		this.canvas.width = width
		this.canvas.height = height
		this.canvas.style.width = this.el.clientWidth + 'px'
		this.canvas.style.height = this.el.clientHeight + 'px'
	}

	onResize() {
		if (this.resizeRaf) {
			return
		}

		this.resizeRaf = requestAnimationFrame(() => {
			this.resizeRaf = 0

			if (!this.hasMeaningfulResize()) {
				return
			}

			this.baseViewportHeight = window.innerHeight

			const nextSet = this.getActiveSet()
			const nextSetKey = this.getActiveSetKey(nextSet)
			const setChanged = nextSetKey !== this.activeSetKey
			this.activeSetKey = nextSetKey

			this.setupCanvasSize()

			if (setChanged) {
				this.webpAssetsMissing = false
				this.stopBackgroundPreload()

				this.images.clear()
				this.loading.clear()
				this.startBackgroundPreload()
			}

			if (setChanged || !this.images.has(this.currentFrame)) {
				this.loadFrame(this.currentFrame)
					.then(() => {
						this.drawFrame(this.currentFrame)
						this.emitStateChange()
					})
					.catch(() => {})
			} else {
				this.drawFrame(this.currentFrame)
				this.emitStateChange()
			}

			if (this.trigger) {
				this.trigger.refresh()
			}
		})
	}
}
